<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { fetchVoucherConfig, uploadMezVouchers } from "./api";

const router = useRouter();
const loading = ref(false), success = ref(false), error = ref("");
const fileName = ref(""), importData = ref([]), entity = ref(""), value = ref("");
const entityList = ref(["Goldheart", "Lee Hwa", "Maxi-Cash"]);
const valueList = ref(["$150", "$250", "$888", "$1588", "$2288", "$2988", "$3688"]);

onMounted(async () => {
  try {
    const cfg = await fetchVoucherConfig();
    if (cfg.entityList) entityList.value = cfg.entityList;
    if (cfg.voucherValueList) valueList.value = cfg.voucherValueList;
  } catch { /* fall back to defaults */ }
});

function handleFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  fileName.value = file.name; importData.value = [];
  // Simple CSV read for .xlsx: if xlsx is not available, fall back to plain CSV parse
  const reader = new FileReader();
  reader.onload = async (ev) => {
    try {
      const { read, utils } = await import("xlsx");
      const wb = read(ev.target.result);
      importData.value = utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
    } catch {
      // Fallback: plain CSV
      const text = new TextDecoder().decode(ev.target.result);
      const lines = text.trim().split(/\r?\n/);
      const headers = lines[0].split(",").map(h => h.trim());
      importData.value = lines.slice(1).map(line => { const v = line.split(","); return Object.fromEntries(headers.map((h, i) => [h, (v[i] ?? "").trim()])); });
    }
    const firstName = importData.value[0]?.["Coupon Name"];
    const match = typeof firstName === "string" ? firstName.match(/\$(\S+)/) : null;
    if (match) value.value = match[0];
  };
  reader.readAsArrayBuffer(file);
}

async function upload() {
  error.value = ""; success.value = false;
  if (!fileName.value || !importData.value.length) { error.value = "Please include a file."; return; }
  if (!entity.value) { error.value = "Entity is required."; return; }
  loading.value = true;
  try {
    const data = importData.value.map(r => r["Coupon No"]);
    await uploadMezVouchers({ data, entity: entity.value, value: parseInt(String(value.value).replace("$", ""), 10) });
    success.value = true; fileName.value = ""; importData.value = []; entity.value = ""; value.value = "";
  } catch { error.value = "Error occurred. Please try again."; }
  finally { loading.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'mezzofy-vouchers' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Upload Mezzofy List</h1>
    <VCard class="pa-4" max-width="520">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Upload complete.</VAlert>
      <div class="mb-4">
        <div class="text-body-2 font-weight-semibold mb-1">Excel File (.xlsx)</div>
        <input type="file" accept=".xlsx,.xls,.csv" @change="handleFile" />
        <p v-if="fileName" class="text-caption text-medium-emphasis mt-1">{{ fileName }} — {{ importData.length }} coupon(s)</p>
      </div>
      <VSelect v-model="entity" label="Entity" :items="entityList" class="mb-4" />
      <VTextField v-model="value" label="Voucher Value" hint="Auto-detected; editable." class="mb-4" />
      <VBtn :loading="loading" @click="upload">Upload</VBtn>
    </VCard>
  </section>
</template>
